import clsx from 'clsx'
import {
  AlertCircle,
  BookOpen,
  Loader2,
  Minus,
  PackageOpen,
  Plus,
  ShieldCheck,
} from 'lucide-react'
import type { ReactNode } from 'react'
import type { TFunction } from 'i18next'
import type { MallCategory } from '@/api/mall'

const KNOWN_CATEGORY_SLUGS = new Set(['textbook', 'teaching-aid', 'promotional', 'marketing'])

export function mallCategoryLabel(category: MallCategory, t: TFunction) {
  if (!KNOWN_CATEGORY_SLUGS.has(category.slug)) return category.name
  return t(`categories.${category.slug}`, { defaultValue: category.name })
}

export function formatMallCurrency(amount: number, language: string, currency = 'KRW') {
  return new Intl.NumberFormat(language === 'en' ? 'en-US' : 'ko-KR', {
    style: 'currency',
    currency,
    maximumFractionDigits: currency === 'KRW' ? 0 : 2,
  }).format(amount)
}

export function formatMallDate(value: string, language: string, includeTime = false) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  if (includeTime) {
    const parts = new Intl.DateTimeFormat(language === 'en' ? 'en-US' : 'ko-KR', {
      timeZone: 'Asia/Seoul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date)
    const valueOf = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((part) => part.type === type)?.value ?? ''
    return `${valueOf('year')}-${valueOf('month')}-${valueOf('day')} ${valueOf('hour')}:${valueOf('minute')}:${valueOf('second')}`
  }
  return new Intl.DateTimeFormat(language === 'en' ? 'en-US' : 'ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date)
}

interface ProductImageProps {
  src: string | null
  alt: string
  className?: string
  eager?: boolean
}

export function MallProductImage({ src, alt, className, eager = false }: ProductImageProps) {
  return (
    <div
      className={clsx(
        'relative overflow-hidden bg-white',
        className,
      )}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          loading={eager ? 'eager' : 'lazy'}
          className="h-full w-full object-contain"
        />
      ) : (
        <div className="flex h-full min-h-44 flex-col items-center justify-center gap-3 text-gray-400">
          <span className="grid size-14 place-items-center rounded-lg border border-gray-200 bg-white">
            <BookOpen className="size-6" strokeWidth={1.5} />
          </span>
          <span className="max-w-40 text-center text-xs font-medium tracking-[0.18em]">{alt}</span>
        </div>
      )}
    </div>
  )
}

export function MallLoading({ label }: { label: string }) {
  return (
    <div className="flex min-h-72 flex-col items-center justify-center gap-3 text-gray-500">
      <Loader2 className="size-7 animate-spin text-primary-500" aria-hidden />
      <p className="text-sm">{label}</p>
    </div>
  )
}

interface EmptyProps {
  title: string
  description?: string
  action?: ReactNode
}

export function MallEmpty({ title, description, action }: EmptyProps) {
  return (
    <div className="flex min-h-64 flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white px-6 text-center">
      <span className="grid size-12 place-items-center rounded-lg bg-gray-100 text-gray-400">
        <PackageOpen className="size-6" strokeWidth={1.5} />
      </span>
      <h2 className="mt-4 font-semibold text-gray-700">{title}</h2>
      {description ? <p className="mt-1 max-w-md text-sm leading-6 text-gray-500">{description}</p> : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  )
}

export function MallError({
  title,
  retryLabel,
  onRetry,
}: {
  title: string
  retryLabel: string
  onRetry: () => void
}) {
  return (
    <div role="alert" className="flex min-h-64 flex-col items-center justify-center rounded-lg border border-red-200 bg-red-50 px-6 text-center">
      <AlertCircle className="size-7 text-red-500" />
      <p className="mt-4 text-sm font-medium text-red-700">{title}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-5 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-700 transition-colors hover:bg-red-100"
      >
        {retryLabel}
      </button>
    </div>
  )
}

export function OwnerOnlyNotice({
  title,
  description,
  compact = false,
}: {
  title: string
  description: string
  compact?: boolean
}) {
  return (
    <div
      className={clsx(
        'flex gap-3 border border-amber-200 bg-amber-50 text-amber-800',
        compact ? 'rounded-lg px-4 py-3' : 'rounded-lg px-5 py-4',
      )}
    >
      <ShieldCheck className="mt-0.5 size-5 shrink-0" />
      <div>
        <p className="text-sm font-semibold">{title}</p>
        <p className="mt-0.5 text-xs leading-5 text-amber-700">{description}</p>
      </div>
    </div>
  )
}

interface QuantityStepperProps {
  value: number
  min?: number
  max?: number
  onChange: (next: number) => void
  disabled?: boolean
  decreaseLabel: string
  increaseLabel: string
}

export function QuantityStepper({
  value,
  min = 1,
  max = 999,
  onChange,
  disabled = false,
  decreaseLabel,
  increaseLabel,
}: QuantityStepperProps) {
  return (
    <div className="inline-flex h-10 items-center overflow-hidden rounded-lg border border-gray-300 bg-white">
      <button
        type="button"
        aria-label={decreaseLabel}
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={disabled || value <= min}
        className="grid size-10 place-items-center text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-35"
      >
        <Minus className="size-4" />
      </button>
      <span className="min-w-10 text-center text-sm font-semibold tabular-nums text-gray-900">{value}</span>
      <button
        type="button"
        aria-label={increaseLabel}
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={disabled || value >= max}
        className="grid size-10 place-items-center text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-35"
      >
        <Plus className="size-4" />
      </button>
    </div>
  )
}
